import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useBlockLayout, useTable } from "react-table";
import { addFacilityTagAsync, deleteFacilityTagAsync, editFacilityTagAsync, getFacilityTagListAsync } from "src/api/building/commonfacility-api";
import { BuildingFacilityTagModel, BuildingFacilityTagRequest } from "src/api/building/commonfacility-types";
import { useApiOperation } from "src/api/hooks";
import { BaseButton } from "src/components";
import useFacilityTag from "src/pages/building/hooks/useFacilityTag";
import { useModal } from "src/recoil/modalState/hook";
import FacilityTagsColumns from "../columns/FacilityTagsColumns";
import FacilityTagFormModal from "./FacilityTagFormModal";

interface Props {
  // openTagFormModal: () => void;
  onClose: () => void;
  getBuildingFacilityList: () => Promise<void>;
}

const BuildingFacilityTagsModal = ({ getBuildingFacilityList, onClose }: Props) => {
  const [facilityTags, setfacilityTags] = useState<BuildingFacilityTagModel[]>([]);

  const { handleTagError } = useFacilityTag();
  // 태그 목록
  const { executeAsync: getFacilityTags } = useApiOperation(getFacilityTagListAsync);
  // 태그 등록
  const { executeAsync: addFacilityTag } = useApiOperation(addFacilityTagAsync, { noErrorModal: true });
  // 태그 수정
  const { executeAsync: editFacilityTag } = useApiOperation(editFacilityTagAsync, { noErrorModal: true });
  // 태그 삭제
  const { executeAsync: deleteFacilityTag } = useApiOperation(deleteFacilityTagAsync);

  // 모달 관리
  const { setBaseModal, setAbstractModalZ2 } = useModal();

  useEffect(() => {
    fetchFacilityTagList();
  }, []);

  // 태그 목록 조회
  const fetchFacilityTagList = async (facilityId?: string) => {
    const { data } = await getFacilityTags({ facilityId: facilityId || "" });

    const result = data?.data?.content;
    setfacilityTags(result || []);
  };

  // 태그 등록
  const handleAddFacilityTag = async (request: BuildingFacilityTagRequest) => {
    const { name } = request;
    const maxPriority = facilityTags.length > 0 ? Math.min(...facilityTags.map((tag) => tag.priority || 0)) : 0;
    const newPriority = maxPriority - 1;

    try {
      await addFacilityTag({ name, priority: newPriority });
    } catch (error) {
      const errorResponse = error instanceof AxiosError ? error.response : undefined;
      handleTagError({ ...errorResponse?.data?.meta, status: errorResponse?.status, cause: error });
      return;
    }

    await fetchFacilityTagList();
    await getBuildingFacilityList();
    setAbstractModalZ2({ isOpen: false });
  };

  // 태그 수정
  const handleEditFacilityTag = async (request: BuildingFacilityTagRequest) => {
    const { name, priority, tagId } = request;
    try {
      await editFacilityTag({ tagId: String(tagId), name, priority: priority! });
    } catch (error) {
      const errorResponse = error instanceof AxiosError ? error.response : undefined;
      handleTagError({ ...errorResponse?.data?.meta, status: errorResponse?.status, cause: error });
      return;
    }

    await fetchFacilityTagList();
    await getBuildingFacilityList();
    setAbstractModalZ2({ isOpen: false });
  };

  // 태그 등록 모달 오픈
  const handleOpenTagFormModal = (tagDetail?: BuildingFacilityTagModel) => {
    setAbstractModalZ2({
      isOpen: true,
      size: "medium",
      children: (
        <FacilityTagFormModal
          tagDetail={tagDetail}
          onSubmit={tagDetail ? handleEditFacilityTag : handleAddFacilityTag}
          onClose={() => setAbstractModalZ2({ isOpen: false })} //
        />
      ),
    });
  };

  // 태그 삭제
  const handleDeleteFacilityTag = async (tagId: string) => {
    const execute = async () => {
      await deleteFacilityTag({ tagId });
      await fetchFacilityTagList();
      await getBuildingFacilityList();

      setAbstractModalZ2({ isOpen: false });
      setBaseModal({ isOpen: false });
    };

    setBaseModal({
      isOpen: true,
      title: "삭제하시겠습니까?",
      children: "해당 태그는 적용된 모든 공용공간에서 삭제됩니다.",
      btnLeftTitle: "취소",
      btnRightTitle: "확인",
      onClick: () => execute(),
      onClose: () => setBaseModal({ isOpen: false }),
    });
  };

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable(
    {
      columns: FacilityTagsColumns as any,
      data: facilityTags ?? [],
      tableHelpers: {
        handleOpenTagFormModal,
        handleDeleteFacilityTag,
        handleEditFacilityTag,
      },
    },
    useBlockLayout,
  );
  return (
    <>
      <section className="base-abstract-modal__title border-bottom-gray  ">
        <h1 className="">태그 관리</h1>
      </section>
      <section className="sub-title my20 px30">
        <div className="flex-center-between">
          <h2 className="font18">태그 목록</h2>
          <div>
            <BaseButton title="태그 등록" onClick={handleOpenTagFormModal} />
          </div>
        </div>
      </section>

      <section className=" px30">
        <div className="">
          <div {...getTableProps()} className="base-table">
            <div className="header">
              {headerGroups.map((headerGroup) => (
                <div {...headerGroup.getHeaderGroupProps()} className="base-table__tr">
                  {headerGroup.headers.map((header) => {
                    return (
                      <div {...header.getHeaderProps()} className="base-table__th flex-center-start">
                        {header.render("Header")}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            <div {...getTableBodyProps()} className="body">
              {rows.map((row) => {
                prepareRow(row);
                return (
                  <div {...row.getRowProps()} className={`base-table__tr`}>
                    {row.cells.map((cell) => {
                      const isMultiLine = (row.original as any)?.times?.length > 1;
                      return (
                        <div {...cell.getCellProps()} className={`base-table__td flex-row ${isMultiLine && "multi-line"}`}>
                          {cell.render("Cell")}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {rows.length === 0 && (
              <div className="base-table__tr table-cursor-unset">
                <div className="base-table__td w-100 text-center">
                  <div className="w-100">
                    <span>데이터가 없습니다.</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="base-abstract-modal__btn-wrap">
        <BaseButton title="완료" onClick={onClose} />
      </section>
    </>
  );
};

export default BuildingFacilityTagsModal;
